import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect } from 'react';
import { View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import { useTranslation } from 'react-i18next';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import type { LiveMatchStats, LiveStatPair, TeamRef } from '@/lib/data/provider';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';

function twoDecimals(value: number): string {
  return (Math.round(value * 100) / 100).toFixed(2);
}

/** The breathing LIVE chip - matches the app's single live accent. */
function PulsingLive({ label }: { label: string }) {
  const reducedMotion = useReducedMotion();
  const pulse = useSharedValue(1);

  useEffect(() => {
    if (!reducedMotion) {
      pulse.value = withRepeat(withTiming(0.35, { duration: 900 }), -1, true);
    }
  }, [pulse, reducedMotion]);

  const dotStyle = useAnimatedStyle(() => ({ opacity: pulse.value }));

  return (
    <View className="flex-row items-center gap-1.5 rounded-full bg-live/10 px-2.5 py-1">
      <Animated.View style={dotStyle}>
        <View className="h-1.5 w-1.5 rounded-full bg-live" />
      </Animated.View>
      <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-live">
        {label}
      </AppText>
    </View>
  );
}

/** One live-stat row: home value · centred label · away value (laid out LTR so
 *  home always owns the left, mirrored naturally by the parent row in RTL). */
function StatRow({ home, away, label }: { home: string; away: string; label: string }) {
  return (
    <View className="flex-row items-center px-4 py-1.5" style={{ direction: 'ltr' }}>
      <AppText variant="caption" weight="bold" tabular className="w-12 text-ink">
        {home}
      </AppText>
      <AppText
        variant="micro"
        weight="medium"
        className="flex-1 text-center text-muted"
        numberOfLines={1}
      >
        {label}
      </AppText>
      <AppText variant="caption" weight="bold" tabular className="w-12 text-right text-ink">
        {away}
      </AppText>
    </View>
  );
}

/**
 * The live xG ticker - a glossy "match is happening now" card pinned to the top
 * of the analytics panel while a fixture is live. It shows a pulsing LIVE badge +
 * elapsed minute, the running score, a live xG split bar (read from
 * `stats.expected_goals`, since the top-level xG can be null early), and a
 * compact strip of the headline live stats (possession, shots, shots on target,
 * corners). Polled every 30s by the caller's `useLiveMatchStats` hook. The
 * caller renders this only when the fixture is live AND a fresh row exists, so
 * there is no internal "not live" branch - but it still degrades cleanly if a
 * given stat key is missing (that pill simply reads 0).
 */
export function LiveXgTicker({
  live,
  homeTeam,
  awayTeam,
}: {
  live: LiveMatchStats;
  homeTeam: TeamRef | null;
  awayTeam: TeamRef | null;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale, arabicNumerals } = useLocale();

  // Live xG comes from the stats map first (top-level can lag/be null early).
  const xgPair = live.stats.expected_goals;
  const homeXg = Math.max(xgPair?.home ?? live.home_xg ?? 0, 0);
  const awayXg = Math.max(xgPair?.away ?? live.away_xg ?? 0, 0);
  const totalXg = homeXg + awayXg;
  const homeShare = totalXg > 0 ? homeXg / totalXg : 0.5;
  const homeLeads = homeXg >= awayXg;

  const homeGoals = live.home_goals ?? 0;
  const awayGoals = live.away_goals ?? 0;

  const num = (value: number) => localizeDigits(String(Math.round(value)), arabicNumerals);
  const pairHome = (pair?: LiveStatPair) => num(pair?.home ?? 0);
  const pairAway = (pair?: LiveStatPair) => num(pair?.away ?? 0);

  // The minute label: elapsed when known, else the LIVE word.
  const minuteLabel =
    typeof live.elapsed === 'number'
      ? `${localizeDigits(String(live.elapsed), arabicNumerals)}′`
      : t('common.live');

  return (
    <Card elevated className="overflow-hidden">
      {/* A faint top-down sheen so the live card reads a touch glossier. */}
      <LinearGradient
        colors={['rgba(34,211,255,0.10)', 'rgba(34,211,255,0)']}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '60%' }}
        pointerEvents="none"
      />

      {/* Header: pulsing LIVE + elapsed minute. */}
      <View className="flex-row items-center justify-between px-4 pt-3">
        <PulsingLive label={t('match.live')} />
        <AppText variant="caption" weight="bold" tabular className="text-live">
          {minuteLabel}
        </AppText>
      </View>

      {/* Score row: crest · goals — goals · crest (mirrors in RTL). */}
      <View className="flex-row items-center justify-between gap-3 px-4 pb-1 pt-3">
        <View className="flex-1 flex-row items-center gap-2">
          <TeamCrest uri={homeTeam?.logo_url} name={localizeTeamName(homeTeam, locale)} size="sm" />
          <AppText variant="title" weight="bold" tabular className="text-ink">
            {localizeDigits(String(homeGoals), arabicNumerals)}
          </AppText>
        </View>
        <AppText variant="title" weight="bold" className="text-muted">
          –
        </AppText>
        <View className="flex-1 flex-row items-center justify-end gap-2">
          <AppText variant="title" weight="bold" tabular className="text-ink">
            {localizeDigits(String(awayGoals), arabicNumerals)}
          </AppText>
          <TeamCrest uri={awayTeam?.logo_url} name={localizeTeamName(awayTeam, locale)} size="sm" />
        </View>
      </View>

      {/* Live xG numbers + split bar. */}
      <View className="flex-row items-center justify-between px-4 pt-2">
        <AppText
          variant="caption"
          weight="bold"
          tabular
          className={homeLeads ? 'text-volt-bright' : 'text-soft'}
        >
          {localizeDigits(twoDecimals(homeXg), arabicNumerals)}
        </AppText>
        <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
          {t('match.xgShort')}
        </AppText>
        <AppText
          variant="caption"
          weight="bold"
          tabular
          className={!homeLeads ? 'text-volt-bright' : 'text-soft'}
        >
          {localizeDigits(twoDecimals(awayXg), arabicNumerals)}
        </AppText>
      </View>
      <View className="px-4 pb-3 pt-2">
        <View
          className="h-2 flex-row overflow-hidden rounded-full bg-surface-2"
          style={{ direction: 'ltr' }}
        >
          <View style={{ flex: Math.max(homeShare, 0.02), backgroundColor: palette.volt }} />
          <View style={{ width: 2, backgroundColor: palette.deep }} />
          <View style={{ flex: Math.max(1 - homeShare, 0.02), backgroundColor: palette.voltDim }} />
        </View>
      </View>

      {/* Key live-stat rows: home · label · away. */}
      <View className="border-t border-line py-1.5">
        <StatRow
          home={`${pairHome(live.stats.ball_possession)}%`}
          away={`${pairAway(live.stats.ball_possession)}%`}
          label={t('match.possession')}
        />
        <StatRow
          home={pairHome(live.stats.total_shots)}
          away={pairAway(live.stats.total_shots)}
          label={t('match.shots')}
        />
        <StatRow
          home={pairHome(live.stats.shots_on_target)}
          away={pairAway(live.stats.shots_on_target)}
          label={t('match.onTarget')}
        />
        <StatRow
          home={pairHome(live.stats.corner_kicks)}
          away={pairAway(live.stats.corner_kicks)}
          label={t('match.corners')}
        />
      </View>
    </Card>
  );
}
